import { brokerService } from 'api/services/broker'
import {
  BrokerListItem,
  GetBrokerListResponseBody,
  GetBrokerReqListItem,
} from 'api/types/broker'
import { Wrapper } from 'components'
import { AppContext } from 'contexts/AppContext'
import { AuthContext } from 'contexts/AuthContext'
import {
  Button,
  Card,
  Input,
  Label,
  Select,
  SelectAction,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectValue,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from 'keep-react'
import { CheckCircle, TrashSimple, WarningCircle } from 'phosphor-react'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { RouteNamesAuth } from 'routes/enum'

interface BrokerQueryVars {
  brokerNo: number | null
  name: string
  status: string | null
}

export const BackofficeBrokers = () => {
  const navigate = useNavigate()
  //first load and is api being called
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [brokers, setBrokers] = useState<BrokerListItem[]>([])
  const [brokerRequests, setBrokerRequests] = useState<
    GetBrokerReqListItem[] | null
  >(null)
  const [brokersData, setBrokersData] = useState<BrokerListItem[]>([])
  const { setCurrentBroker } = useContext(AuthContext)
  const { setBackOfficeMode } = useContext(AppContext)
  const [queryVars, setQueryVars] = useState<BrokerQueryVars>({
    brokerNo: null,
    name: '',
    status: null,
  })

  const getBrokerList = async () => {
    setIsLoading(true)
    try {
      const response =
        (await brokerService.getBrokerList()) as GetBrokerListResponseBody
      setBrokers(
        (response.Broker || []).sort((a, b) => a.Name.localeCompare(b.Name)),
      )
      setBrokersData(
        (response.Broker || []).sort((a, b) => a.Name.localeCompare(b.Name)),
      )
      //api call to get broker list
    } catch (error) {
      console.error('Error fetching broker list:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getBrokerReqList = async () => {
    try {
      const response = await brokerService.getBrokerReqList()
      setBrokerRequests(response)
    } catch (error) {
      console.error('Error fetching broker req list:', error)
    }
  }

  const deleteBrokerReq = async (id: number) => {
    try {
      await brokerService.deleteBrokerReq({ id })
      setBrokerRequests(prev =>
        prev ? prev.filter(request => request.id !== id) : null,
      )
      toast.success('Pedido de nomeação eliminado com sucesso.')
    } catch (error) {
      toast.error('Erro ao eliminar o pedido de nomeação.')
      console.error('Error deleting broker req:', error)
    }
  }

  useEffect(() => {
    let filteredBrokers = brokers

    if (queryVars.brokerNo) {
      filteredBrokers = filteredBrokers.filter(
        broker => broker.BrokerNo === queryVars.brokerNo,
      )
    }

    if (queryVars.name) {
      filteredBrokers = filteredBrokers.filter(broker =>
        broker.Name.toLowerCase().includes(queryVars.name.toLowerCase()),
      )
    }

    if (queryVars.status) {
      const isActive = queryVars.status === 'Active'
      filteredBrokers = filteredBrokers.filter(
        broker => broker.Active === isActive,
      )
    }

    setBrokersData(filteredBrokers)
  }, [queryVars, brokers])

  useEffect(() => {
    getBrokerList()
    getBrokerReqList()
  }, [])

  return (
    <div className="py-12">
      <Wrapper>
        <h1 className="pb-10 text-xxxl font-medium text-metal-900 flex items-center justify-between">
          Backoffice Brokers
        </h1>
        {isLoading ? (
          <Card className="p-4 w-full max-w-full">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : (
          <>
            {brokerRequests && brokerRequests.length > 0 && (
              <>
                <h2 className="text-xl font-medium mb-4">
                  Pedidos de nomeação
                </h2>
                <Card className="w-full max-w-full p-4 md:p-6 mb-6">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Empresa</TableHead>
                        <TableHead>Nome do Administrador</TableHead>
                        <TableHead>NIF</TableHead>
                        <TableHead>Telemóvel</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Ficheiros</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {brokerRequests?.map(request => (
                        <TableRow key={request.id}>
                          <TableCell>{request.company}</TableCell>
                          <TableCell>{request.adminName}</TableCell>
                          <TableCell>{request.nif}</TableCell>
                          <TableCell>{request.mobile}</TableCell>
                          <TableCell>{request.email}</TableCell>
                          <TableCell>
                            <ul className="list-disc">
                              {request.files ? (
                                request.files.map((file, index) => (
                                  <li className="whitespace-nowrap" key={index}>
                                    <a
                                      className="text-metal-600 underline"
                                      href={file.uri}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      {file.label || `Ficheiro ${index}`}
                                    </a>
                                  </li>
                                ))
                              ) : (
                                <li>Não</li>
                              )}
                            </ul>
                          </TableCell>
                          <TableCell>
                            <Button
                              color="secondary"
                              variant="softBg"
                              onClick={() => deleteBrokerReq(request.id)}
                            >
                              <TrashSimple />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>
              </>
            )}
            <h2 className="text-xl font-medium mb-4">Brokers</h2>
            <Card className="w-full max-w-full p-4 md:p-6 mb-6">
              <div className="grid grid-col-1 md:grid-cols-3 gap-4 md:gap-6 w-full items-end">
                <div>
                  <Label className="text-body-4 block pb-2">BrokerNo</Label>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      value={queryVars.brokerNo || ''}
                      onChange={e =>
                        setQueryVars({
                          ...queryVars,
                          brokerNo: Number(e.target.value) || null,
                        })
                      }
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-body-4 block pb-2">Nome</Label>
                  <div className="flex items-center gap-1">
                    <Input
                      type="text"
                      value={queryVars.name}
                      onChange={e =>
                        setQueryVars({
                          ...queryVars,
                          name: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-body-4 block pb-2">Estado</Label>
                  <Select
                    name="estado"
                    onValueChange={val => {
                      setQueryVars({
                        ...queryVars,
                        status: val === 'none' ? '' : val,
                      })
                    }}
                  >
                    <SelectAction className="[&_span]:truncate [&_span]:max-w-[90%]">
                      <SelectValue placeholder="Estado" />
                    </SelectAction>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="none">Todos</SelectItem>
                        <SelectItem value="Active">Ativo</SelectItem>
                        <SelectItem value="Inactive">Inativo</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-4">
                <Button
                  color="secondary"
                  variant="outline"
                  onClick={() => {
                    setQueryVars({
                      brokerNo: null,
                      name: '',
                      status: null,
                    })
                  }}
                >
                  Reset dos filtros
                </Button>
              </div>
            </Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>BrokerNo</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Segmento</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {brokersData.map(broker => (
                  <TableRow key={broker.BrokerNo}>
                    <TableCell>{broker.BrokerNo}</TableCell>
                    <TableCell>{broker.Name}</TableCell>
                    <TableCell
                      className={
                        broker.Active ? 'text-green-500' : 'text-red-500'
                      }
                    >
                      {broker.Active ? (
                        <CheckCircle size={20} weight="fill" />
                      ) : (
                        <WarningCircle size={20} weight="fill" />
                      )}
                    </TableCell>
                    <TableCell>{broker.Segmentation}</TableCell>
                    <TableCell className="flex flex-col gap-2">
                      <Button
                        color="secondary"
                        variant="softBg"
                        size="xs"
                        onClick={() =>
                          navigate(
                            RouteNamesAuth.BACKOFFICE_BROKER_DETAILS.replace(
                              ':id',
                              broker.BrokerNo.toString(),
                            ),
                          )
                        }
                      >
                        Ver Detalhes
                      </Button>
                      <Button
                        color="primary"
                        variant="softBg"
                        size="xs"
                        onClick={() => {
                          setCurrentBroker({
                            BrokerNo: broker.BrokerNo,
                            BrokerName: broker.Name,
                            IsGroup: false,
                          })
                          setBackOfficeMode(false)
                          navigate(RouteNamesAuth.DASHBOARD)
                        }}
                      >
                        Emular
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </Wrapper>
    </div>
  )
}
